import type { BaseClient } from '../../http/BaseClient.js';
import type {
  ActivateRequest,
  ExchangeCodeRequest,
  LoginRequest,
  PublicConfig,
  RefreshRequest,
  RegisterRequest,
  ResetPasswordRequest,
  SessionResponse,
  SimpleResponse,
} from './types.js';

export class AuthClient {
  constructor(private readonly http: BaseClient) {}

  login(body: LoginRequest): Promise<SessionResponse> {
    return this.http.post<SessionResponse>('/user/login', { body, skipAuth: true });
  }

  register(body: RegisterRequest): Promise<SessionResponse> {
    return this.http.post<SessionResponse>('/user/register', { body, skipAuth: true });
  }

  /**
   * Unauthenticated boot config. The SPA reads `openRegistration` to decide
   * whether to render the self-service signup form (false => invite-only /
   * preview mode). Safe to call before any session exists.
   */
  publicConfig(): Promise<PublicConfig> {
    return this.http.get<PublicConfig>('/public-config', { skipAuth: true });
  }

  logout(refreshToken?: string): Promise<SimpleResponse> {
    return this.http.post<SimpleResponse>('/user/logout', {
      body: { refreshToken },
    });
  }

  refresh(body: RefreshRequest): Promise<SessionResponse> {
    return this.http.post<SessionResponse>('/user/refresh', { body, skipAuth: true });
  }

  activate(body: ActivateRequest): Promise<SessionResponse> {
    return this.http.post<SessionResponse>('/user/activate', { body, skipAuth: true });
  }

  requestVerification(email: string): Promise<SimpleResponse> {
    return this.http.post<SimpleResponse>('/user/request-verification', {
      body: { email },
      skipAuth: true,
    });
  }

  requestPasswordReset(email: string): Promise<SimpleResponse> {
    return this.http.post<SimpleResponse>('/user/request-reset-password', {
      body: { email },
      skipAuth: true,
    });
  }

  resetPassword(body: ResetPasswordRequest): Promise<SimpleResponse> {
    return this.http.post<SimpleResponse>('/user/reset-password', { body, skipAuth: true });
  }

  exchangeGoogle(body: ExchangeCodeRequest): Promise<SessionResponse> {
    return this.http.post<SessionResponse>('/auth/google/exchange', { body, skipAuth: true });
  }
}
